import React, { PureComponent } from 'react';
import { Flex } from '../../common/Flex';
import { Button, Col, Divider, notification, Row, Select, Space } from 'antd';
import {
    CapacityModel,
    CountsAlgorithm,
    CountType,
    FlightDto,
    FORCE_FLIGHT_IN_REGULATION_DTO_CLAZZ_VALUE,
    ForceEntryType,
    ForceFlightInRegulationDto,
    ForceFlightInRegulationDto_ForceType,
    MonitoredLoadValue,
    MonitoredTv,
    REGULATION_LIST_REQUEST_DTO_CLAZZ_VALUE,
    RegulationDto,
    RegulationListRequestDto,
    StandardCountSubtotalsType,
    TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    TRAFFIC_MONITOR_REQUEST_CLAZZ_VALUE,
    TrafficCountsByTvRequestDto,
    TrafficCountsDto,
    TrafficMonitorRequest,
    TrafficType,
    UpdateFlightsInMeasureRequestDto,
} from '../../../model/CORE';
import TimeUtils from '../../../common/util/TimeUtils';
import { ModalPanelProps } from '../DashboardModal';
import AppContext from '../../../common/context/AppContext';
import { CountsGraph } from '../tv/graph/CountsGraph';
import TrafficCountUtils from '../../../common/util/TrafficCountUtils';
import { EntryLoadBar } from '../../dashboard/network/flows/svg/EntryLoadBar';
import MonitoredLoadUtils from '../../../common/util/MonitoredLoadUtils';
import { ServiceFactory } from '../../../service/ServiceFactory';
import MeasureUtils from '../../../common/util/MeasureUtils';
import CapacityUtils from '../../../common/util/CapacityUtils';
import {
    FlightNetworkImprovementReply,
    FlightNetworkImprovementRequest,
    FORCE_CTOT_IMPROVEMENT_CLAZZ_VALUE,
    ForceCtotImprovement,
} from '../../../model/SOL49';
import { DoubleLeftOutlined, DoubleRightOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import memoizeOne from 'memoize-one';
import './NetworkImprovementPanel.less';

interface NetworkImprovementPanelProps extends ModalPanelProps {
    flight: FlightDto;
}

interface NetworkImprovementPanelState {
    newCtot: string;
    regulation: RegulationDto;
    entryCounts: TrafficCountsDto;
    monitoredTv: MonitoredTv;
    niaResult: FlightNetworkImprovementReply;
}

const INITIAL_STATE: NetworkImprovementPanelState = {
    newCtot: null,
    regulation: null,
    entryCounts: null,
    monitoredTv: null,
    niaResult: null,
};

const DEFAULT_ENTRY_STEP = 60;
const DEFAULT_ENTRY_INTERVAL = 20;
const DEFAULT_ENTRY_TRAFFIC_TRYPE = TrafficType.LOAD;

export class NetworkImprovementPanel extends PureComponent<NetworkImprovementPanelProps, NetworkImprovementPanelState> {
    private lastUpdate: Date;
    public static INITIAL_WIDTH = '60em';

    constructor(props) {
        super(props);
        this.state = Object.assign({}, INITIAL_STATE, {
            newCtot: TimeUtils.formatHHMM(this.props.flight?.calculatedTakeOffTime),
        });
    }
    componentDidMount(): void {
        this.refresh();
    }
    componentDidUpdate(prevProps) {
        if (this.props?.flight !== prevProps?.flight) {
            this.setState(
                Object.assign({}, INITIAL_STATE, {
                    newCtot: TimeUtils.formatHHMM(this.props.flight?.calculatedTakeOffTime),
                }),
                () => this.refresh(),
            );
        }
    }
    refresh = () => {
        if (this.props?.flight) {
            if (!this.state.regulation) {
                this.refreshRegulation();
            } else {
                this.refreshEntryCounts();
                this.refreshTrafficMonitorData();
            }
        }
    };
    refreshRegulation = () => {
        const req: RegulationListRequestDto = {
            clazz: REGULATION_LIST_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            timePeriod: AppContext.getTodayPeriod(),
            fields: MeasureUtils.ALL_REGULATION_FIELDS,
            regulIds: [this.props.flight.mostPenalisingRegulation],
        };
        ServiceFactory.coreService()
            .getRegulations(req)
            .then(reply => {
                if (reply.data?.measures?.length) {
                    this.setState({ regulation: reply.data.measures[0] }, () => this.refresh());
                } else {
                    notification.error({ message: 'Error', description: 'Failed to retrieve regulation', duration: 0 });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };
    refreshEntryCounts = () => {
        if (!this.state.regulation) return;

        const req: TrafficCountsByTvRequestDto = {
            clazz: TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            trafficTypes: [DEFAULT_ENTRY_TRAFFIC_TRYPE],
            trafficWindow: AppContext.getMonitoringPeriod(),
            algorithm: CountsAlgorithm.PSEUDO_NM,
            countType: CountType.ENTRY,
            durationInMin: DEFAULT_ENTRY_STEP,
            intervalInMin: DEFAULT_ENTRY_INTERVAL,
            includeForecastFlights: false,
            subtotalsType: StandardCountSubtotalsType.NM,
            tvId: MeasureUtils.getTvLocation(this.state.regulation)?.id,
            computeHourlyEntryRates: true,
        };
        ServiceFactory.coreService()
            .getTrafficCounts(req)
            .then(reply => {
                if (reply.data) {
                    this.setState({ entryCounts: reply.data });
                } else {
                    notification.error({
                        message: 'Error',
                        description: 'Failed to retrieve regulation list',
                        duration: 0,
                    });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };
    refreshTrafficMonitorData = () => {
        const req: TrafficMonitorRequest = {
            clazz: TRAFFIC_MONITOR_REQUEST_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            adhocTvList: [MeasureUtils.getTvLocation(this.state.regulation)?.id],
            capacityModel: AppContext.getCapacityModel(),
            entryCountsAlgorithm: CountsAlgorithm.PSEUDO_NM,
            occupancyCountsAlgorithm: CountsAlgorithm.PSEUDO_NM,
            timePeriod: AppContext.getMonitoringPeriod(),
            includeForecastFlights: false,
        };
        ServiceFactory.coreService()
            .getTrafficMonitorData(req)
            .then(reply => {
                this.setState({ monitoredTv: reply.data?.monitoredTvSets[0].monitoredTvs[0] });
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };
    getActivePeriods = memoizeOne((monitoredLoadValues: Array<MonitoredLoadValue>) => {
        return monitoredLoadValues && MonitoredLoadUtils.getActivePeriods(monitoredLoadValues);
    });
    renderEntryCounts = () => {
        if (!this.state.entryCounts) return;

        const hourlyEntryCapacity =
            AppContext.getCapacityModel() === CapacityModel.WCM_NRP
                ? CapacityUtils.getWcmCapacity(
                      this.state.entryCounts?.hourlyEntryCapacity,
                      this.state.entryCounts?.timeRange,
                  )
                : this.state.entryCounts?.hourlyEntryCapacity;

        return (
            <CountsGraph
                currentTime={AppContext.getCurrentTime()}
                counts={
                    this.state.entryCounts &&
                    TrafficCountUtils.getCountsFor(this.state.entryCounts, DEFAULT_ENTRY_TRAFFIC_TRYPE)
                }
                activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                timeRange={this.state.entryCounts?.timeRange}
                hourlyEntryCapacity={hourlyEntryCapacity}
                regulationPeriods={[MeasureUtils.getRegulationPeriods(this.state.regulation)]}
                //timeOver={this.state.timeOver}
                //onTimeOverChange={this.handleTimeOverChange}
                //selectedPeriod={this.props.selectedPeriodType === CountType.ENTRY && this.props.selectedPeriod}
                //onSelectedPeriodChange={this.handleSelectedPeriodChange(CountType.ENTRY)}
            />
        );
    };
    renderEntryLoad = () => {
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    E
                </Flex>
                <Flex direction="vertical">
                    <EntryLoadBar
                        loadValues={this.state.monitoredTv?.loadValues}
                        monitoredPeriod={this.state.entryCounts?.timeRange}
                        activePeriods={MonitoredLoadUtils.getActivePeriods(this.state.monitoredTv?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                </Flex>
            </Flex>
        );
    };
    /*renderOccupancyLoad = () => {
        let occLoad = (this.state.monitoredTv?.occupancyLoadValues?.length > 0 && this.state.monitoredTv?.occupancyLoadValues[0].occupancyLoadValues) || []
        let timeRange = undefined // TODO: Get OTMVs
        return (
            <Flex>
                <Flex size='30px' style={{fontWeight: 'bold', justifyContent: 'flex-end', alignItems: 'center', paddingRight: '5px'}}>
                    O
                </Flex>
                <Flex direction='vertical'>
                    <OccupancyLoadBar
                        loadValues={occLoad}
                        monitoredPeriod={this.state.occCounts?.timeRange}
                        activePeriods={MonitoredLoadUtils.getActivePeriods(occLoad)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                </Flex>
            </Flex>
        )
    }*/

    handleNewCtotChange = (newCtot: string) => {
        this.setState({ newCtot: newCtot });
    };

    handleApply = () => {
        const newCtot = TimeUtils.parseHHMM(
            this.state.newCtot,
            TimeUtils.withoutTimeOfDay(this.props.flight?.calculatedTakeOffTime),
        );

        const request = {
            dataset: AppContext.getDataset(),
            day: AppContext.getMonitoredDay(),
            flights: [
                {
                    clazz: FORCE_FLIGHT_IN_REGULATION_DTO_CLAZZ_VALUE,
                    flightProposal: false,
                    forceType: ForceFlightInRegulationDto_ForceType.TOT,
                    immediatelyUnforce: false,
                    flightKeys: this.props.flight?.flightId?.flightKeys,
                    newCtot: newCtot,
                    regulationId: this.props.flight?.mostPenalisingRegulation,
                } as ForceFlightInRegulationDto,
            ],
        } as UpdateFlightsInMeasureRequestDto;

        ServiceFactory.stamCoreService()
            .updateFlightsInMeasure(request)
            .then(reply => {
                if (reply.data) {
                    if (reply.data.processed?.length) {
                        this.props.onClose?.();
                    } else {
                        notification.error({
                            message: 'Error',
                            description: reply.data.failures[0].reason,
                            duration: 0,
                        });
                    }
                    console.log(reply);
                } else {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };
    handleSimulateChange = () => {
        const newCtot = TimeUtils.parseHHMM(
            this.state.newCtot,
            TimeUtils.withoutTimeOfDay(this.props.flight?.calculatedTakeOffTime),
        );
        const request = {
            flightImprovement: {
                clazz: FORCE_CTOT_IMPROVEMENT_CLAZZ_VALUE,
                flightId: this.props.flight?.flightId,
                forceEntryTime: newCtot,
                forceEntryType: ForceEntryType.TOT,
                regulationId: this.props.flight?.mostPenalisingRegulation,
            } as ForceCtotImprovement,
            datasetReference: AppContext.getDatasetReference(),
        } as FlightNetworkImprovementRequest;

        ServiceFactory.sol49Service()
            .getFlightNetworkImprovement(request)
            .then(reply => {
                this.setState({ niaResult: reply.data });
                console.log(reply);
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };

    renderNewCtot = () => {
        const delayMn = Math.trunc(this.props.flight?.delayInMs / TimeUtils.MIN_TO_MS);
        const ctot = this.props.flight?.calculatedTakeOffTime;
        return (
            <Select value={this.state.newCtot} onChange={this.handleNewCtotChange} style={{ width: '6em' }}>
                {Array.from(Array(delayMn + 1).keys()).map(idx => {
                    const time = TimeUtils.formatHHMM(TimeUtils.addMinutes(ctot, idx - delayMn));
                    return (
                        <Select.Option key={`newCtot-${time}`} value={time}>
                            {time}
                        </Select.Option>
                    );
                })}
            </Select>
        );
    };

    renderDeltaDelay = (deltaDelayMn: number) => {
        if (deltaDelayMn) {
            let color = 'white';
            let prefix = '';
            if (deltaDelayMn > 0) {
                color = 'red';
                prefix = '+';
            } else if (deltaDelayMn < 0) {
                color = 'green';
            }
            return (
                <span style={{ fontSize: '1.2em', fontWeight: 'bold', color: color }}>{prefix + deltaDelayMn} min</span>
            );
        }
    };

    renderImprovementSummary = () => {
        if (!this.state.niaResult) return undefined;

        const flightDeltaDelays = this.state.niaResult.flightDeltaDelays.filter(value => value.deltaDelayMs < 0);
        return (
            <Flex style={{ backgroundColor: 'white', alignItems: 'center' }}>
                <Flex size="50%">
                    <span style={{ fontSize: '1.2em', fontWeight: 'bold', color: 'green' }}>
                        {flightDeltaDelays.length}
                    </span>{' '}
                    flights improved
                </Flex>
                <Flex size="50%"></Flex>
            </Flex>
        );
    };

    renderFlightImprovements = () => {
        if (!this.state.niaResult) return undefined;

        return this.state.niaResult.flightDeltaDelays
            .filter(value => value.deltaDelayMs < 0)
            .map(value => {
                return (
                    <Row gutter={[16, 24]}>
                        <Col span={10}>
                            <div>
                                <b>{value.flightId?.flightKeys?.callsign}</b>
                            </div>
                        </Col>
                        <Col span={7}>
                            <div></div>
                        </Col>
                        <Col span={7}>
                            <div>{Math.trunc(value.deltaDelayMs / TimeUtils.MIN_TO_MS)}min</div>
                        </Col>
                    </Row>
                );
            });
    };

    render() {
        const callsign = this.props.flight?.flightId?.flightKeys?.callsign;
        const mpr = this.props.flight?.mostPenalisingRegulation;
        const delayBeforeMn =
            this.props.flight?.delayInMs && Math.trunc(this.props.flight?.delayInMs / TimeUtils.MIN_TO_MS);
        const newCtot = TimeUtils.parseHHMM(
            this.state.newCtot,
            TimeUtils.withoutTimeOfDay(this.props.flight?.calculatedTakeOffTime),
        );
        const delayAfterMn =
            delayBeforeMn +
            ((newCtot && TimeUtils.diffInMinutes(newCtot, this.props.flight?.calculatedTakeOffTime)) || 0);

        const ctotBefore = TimeUtils.formatHHMM(this.props.flight?.calculatedTakeOffTime);

        return (
            <Flex className="network-improvement" direction="vertical">
                <Flex className="modal-title">
                    <Flex size="15em" style={{ fontSize: '0.9em' }}>
                        last update{' '}
                        {TimeUtils.formatDateDDMMYYYY(this.lastUpdate, '-') +
                            ' ' +
                            TimeUtils.formatHHMM(this.lastUpdate)}
                    </Flex>
                    <Flex direction="vertical">
                        <Flex style={{ fontWeight: 'bold', justifyContent: 'center' }}>NETWORK IMPROVEMENT</Flex>
                        <Flex style={{ justifyContent: 'center' }}>{callsign}</Flex>
                    </Flex>
                    <Flex size="15em"></Flex>
                </Flex>
                <Flex size="40em" direction="vertical" style={{ padding: '1em 3em' }}>
                    <Flex size="10em" style={{ padding: '1em 0' }}>
                        <Flex size="40%" direction="vertical">
                            <Flex
                                style={{
                                    alignItems: 'center',
                                    fontSize: '1.2em',
                                    fontWeight: 'bold',
                                    flex: '0 0 auto',
                                }}>
                                <span>Force CTOT</span>
                                <span style={{ paddingLeft: '2em', verticalAlign: 'super', fontSize: '0.7em' }}>
                                    {mpr}
                                </span>
                            </Flex>
                            <Flex style={{ alignItems: 'center' }}>{this.renderNewCtot()}</Flex>
                            <Flex style={{ alignItems: 'center', flex: '0 0 auto' }}>
                                <span style={{ paddingRight: '1em' }}>Flight delay</span>
                                {this.renderDeltaDelay(delayAfterMn - delayBeforeMn)}
                            </Flex>
                        </Flex>
                        <Divider
                            type="vertical"
                            style={{ height: '100%', borderLeft: '0.25em solid rgba(0, 0, 0, 0.35)' }}
                        />
                        <Flex direction="vertical" style={{ fontWeight: 'bold' }}>
                            <Flex size="50%">
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.90em' }}>delay before</div>
                                    <div style={{ lineHeight: '2em', fontSize: '1.2em' }}>{delayBeforeMn}</div>
                                </Col>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.90em' }}>CTOT before</div>
                                    <div style={{ lineHeight: '2em', fontSize: '1.2em' }}>{ctotBefore}</div>
                                </Col>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.90em' }}>CTO before</div>
                                    <div style={{ lineHeight: '2em', fontSize: '1.2em' }}>{}</div>
                                </Col>
                            </Flex>
                            <Flex>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.90em' }}>delay after</div>
                                    <div style={{ lineHeight: '2em', fontSize: '1.2em' }}>{delayAfterMn}</div>
                                </Col>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.90em' }}>CTOT after</div>
                                    <div style={{ lineHeight: '2em', fontSize: '1.2em' }}>
                                        <DoubleLeftOutlined />
                                        <LeftOutlined />
                                        {this.state.newCtot}
                                        <RightOutlined />
                                        <DoubleRightOutlined />
                                    </div>
                                </Col>
                                <Col span={8} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.90em' }}>CTO after</div>
                                    <div style={{ lineHeight: '2em', fontSize: '1.2em' }}>{}</div>
                                </Col>
                            </Flex>
                        </Flex>
                    </Flex>
                    <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                        <Flex size="20em">{this.renderEntryCounts()}</Flex>
                        <Flex size="1em">{this.renderEntryLoad()}</Flex>
                    </Flex>
                </Flex>
                {/*<Divider type='horizontal' style={{width: '100%', borderTop: '0.25em solid rgba(0, 0, 0, 0.35)'}}/>
                <Flex size='40em' direction='vertical' style={{padding: '1em 2em'}}>
                    <Flex size='30%'>
                    </Flex>
                    <Flex size='20%' style={{padding: '1em 0'}}>
                        <Flex size='50%' direction='vertical'>
                            {this.renderImprovementSummary()}
                        </Flex>
                        <Flex size='50%' direction='vertical'>

                        </Flex>
                    </Flex>
                    <Flex size='50%'>
                        <Flex size='50%' direction='vertical'>
                            <Row gutter={[16, 24]}>
                                <Col span={10}>
                                    <div>most improved airlines</div>
                                </Col>
                                <Col span={7}>
                                    <div></div>
                                </Col>
                                <Col span={7}>
                                    <div>average gain</div>
                                </Col>
                            </Row>
                            {this.renderFlightImprovements()}
                        </Flex>
                        <Divider type='vertical' style={{height: '100%', borderLeft: '0.25em solid rgba(0, 0, 0, 0.35)'}}/>
                        <Flex size='50%' direction='vertical'>
                            B
                        </Flex>
                    </Flex>
                </Flex>*/}
                <Flex style={{ alignSelf: 'flex-end', paddingTop: '1em' }}>
                    <NetworkImprovementButtonPanel
                        onClose={this.props.onClose}
                        shareButtonDisabled={true}
                        printButtonDisabled={true}
                        onApply={this.handleApply}
                        applyButtonDisabled={ctotBefore === this.state.newCtot}
                    />
                </Flex>
            </Flex>
        );
    }
}

interface NetworkImprovementButtonPanelProps extends ModalPanelProps {
    onShare?(): void;
    shareButtonDisabled?: boolean;
    onPrint?(): void;
    printButtonDisabled?: boolean;
    onApply?(): void;
    applyButtonDisabled?: boolean;
}

class NetworkImprovementButtonPanel extends PureComponent<NetworkImprovementButtonPanelProps> {
    render() {
        return (
            <Space>
                <Button key="cancel" onClick={this.props.onClose}>
                    Close
                </Button>
                <Button key="share" onClick={this.props.onShare} disabled={this.props.shareButtonDisabled}>
                    Share
                </Button>
                <Button key="print" onClick={this.props.onPrint} disabled={this.props.printButtonDisabled}>
                    Print
                </Button>
                <Button key="apply" onClick={this.props.onApply} disabled={this.props.applyButtonDisabled}>
                    Apply change
                </Button>
            </Space>
        );
    }
}
